import { ChangeDetectorRef, Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CalendarView,
  CapacityCalendarComponent,
  CapacityCrewGroup,
  CapacityDay,
  CapacityDayClickEvent,
  LegendComponent,
  LegendItem,
  resolveCapabilityScope,
} from '@groundworks/ngx-groundworks-ui';
import { CalendarRole, DEFAULT_CALENDAR_ROLES } from '../../models/calendar-role';
import { CALENDAR_ROLES, observeUserRoles$ } from '../../models/roles.model';
import { BranchInfo, CrewScheduleType, DayCapacityInfo, MonthlyCapacityCrewInfo } from '../../sdk/sdk';
import { BranchService } from '../../services/branch.service';
import { CalendarService } from '../../services/calendar.service';
import { formatUtcIsoDate, parseLocalIsoDate } from '../../utils/iso-date';
import { CapacityService } from '../../services/capacity.service';
import { InstallDateFormData } from '../../models/install-event.types';
import { ScheduleProjectPanelComponent } from '../schedule-project-panel/schedule-project-panel.component';
import { SideModalComponent } from '../side-modal/side-modal.component';
import { MonthlyCapacityMetricsComponent } from './monthly-capacity-metrics/monthly-capacity-metrics.component';

// Mirrors the API enum value sent by the server for visiting crews. Comparing
// against this string lets a misalignment between SDK enum and wire format
// (e.g. an SDK regen lag) fail loud rather than silently miscategorise crews.
const API_STATUS_VISITING = 'visiting';

@Component({
  selector: 'app-monthly-capacity',
  imports: [
    LegendComponent,
    CapacityCalendarComponent,
    SideModalComponent,
    MonthlyCapacityMetricsComponent,
    ScheduleProjectPanelComponent,
  ],
  templateUrl: './monthly-capacity.component.html',
  host: { class: 'd-flex flex-column flex-grow-1 overflow-hidden' },
})
export class MonthlyCapacityComponent {
  private readonly capacityService = inject(CapacityService);
  private readonly calendarService = inject(CalendarService);
  private readonly branchService = inject(BranchService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(SideModalComponent) private readonly sideModal!: SideModalComponent;

  hasAccess = false;

  legendItems: LegendItem[] = this.capacityService.getLegendItems();
  /** Filtered view rendered to the calendar. Null while loading or when no
   * branch is selected. */
  crewGroups: CapacityCrewGroup[] | null = null;
  isLoading = false;
  isError = false;
  initialDate: Date | null = null;
  readonly today = new Date();
  /** Mirrors WeekViewCalendarComponent.sectionOrder so /capacity and /calendar
   * render section headers in the same order. */
  readonly sectionOrder: string[] = ['Sub-Contractor', 'Co-Foreman', 'Field Superintendent'];

  selectedBranch: BranchInfo | null = null;
  opportunityId: string | null = null;
  projectId: string | null = null;
  customerSatisfactionVisit = false;
  startDate: Date | null = null;
  endDate: Date | null = null;
  panelInstallDates: InstallDateFormData[] = [];

  /** Raw mapped crews from the most recent load, before filtering. The filter
   * pipeline reads from here every time selection signals change. */
  private allCrewGroups: CapacityCrewGroup[] | null = null;

  /** Filter selections, kept in sync with CalendarService BehaviorSubjects.
   * Defaults match `CrewScheduleStore` so the two pages start identical. */
  private selectedForemenIds: readonly string[] = [];
  private selectedRoles: readonly CalendarRole[] = [...DEFAULT_CALENDAR_ROLES];
  private selectedAbilities: readonly string[] = [];

  /** Role classification per crew, keyed by foremanUserId — derived from the
   * API `department` field at map-time. Visiting crews are not present here
   * (they bypass the role gate). Mirrors CrewScheduleStore. */
  private readonly crewRoles = new Map<string, CalendarRole>();
  /** canInstallProjectTypes per crew. Missing entry fails the ability gate
   * deterministically rather than silently admit such crews. */
  private readonly crewAbilities = new Map<string, readonly string[]>();
  /** Data short-circuit per crew: true when any day in the loaded range has
   * `isAvailable` (server-derived availability anchor) OR `hasInstall` (an
   * install date on this foreman). Stored per-crew because the rendered
   * `CapacityDay` from @groundworks/ngx-groundworks-ui does not expose either
   * flag — keeping the side-map mirrors how role/ability data flows. */
  private readonly crewHasData = new Map<string, boolean>();

  get schedulingMode(): boolean {
    return !!this.opportunityId && !!this.projectId;
  }

  constructor() {
    observeUserRoles$()
      .pipe(takeUntilDestroyed())
      .subscribe((roles) => {
        this.hasAccess = CALENDAR_ROLES.some(role => roles[role]);
        this.cdr.markForCheck();
      });

    this.branchService
      .getSelectedBranch()
      .pipe(takeUntilDestroyed())
      .subscribe((branch) => {
        this.selectedBranch = branch;
        if (!branch) {
          this.crewGroups = null;
          this.allCrewGroups = null;
          this.cdr.markForCheck();
          return;
        }
        this.loadMonthlyCapacity();
      });

    this.calendarService
      .getSelectedForemenIds()
      .pipe(takeUntilDestroyed())
      .subscribe((ids) => {
        this.selectedForemenIds = ids;
        this.applyForemanFilter();
      });

    this.calendarService
      .getSelectedRoles()
      .pipe(takeUntilDestroyed())
      .subscribe((roles) => {
        this.selectedRoles = roles;
        this.applyForemanFilter();
      });

    this.calendarService
      .getSelectedAbilities()
      .pipe(takeUntilDestroyed())
      .subscribe((abilities) => {
        this.selectedAbilities = abilities;
        this.applyForemanFilter();
      });

    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const branchId = params.get('branchId') ?? params.get('branch');
      const startDateParam = params.get('startDate');
      this.opportunityId = params.get('opportunityId');
      this.projectId = params.get('projectId');
      this.customerSatisfactionVisit = params.get('customerSatisfactionVisit') === 'true';
      this.cdr.markForCheck();

      if (branchId) {
        // Same-branch refresh: don't re-fetch / re-write a selection that already
        // matches. Otherwise hydrate via setSelectedBranchById so the deep-link
        // path gets full BranchInfo (id + name + ...) instead of a partial stub.
        const currentBranch = this.branchService.getCurrentSelectedBranch();
        if (currentBranch?.id !== branchId) {
          this.branchService.setSelectedBranchById(branchId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }
      }
      if (startDateParam) {
        this.initialDate = new Date(startDateParam + 'T00:00:00');
        this.cdr.markForCheck();
      }
    });
  }

  onPanelSaved(): void {
    this.loadMonthlyCapacity();
  }

  onPanelExitEditMode(): void {
    this.opportunityId = null;
    this.projectId = null;
    this.panelInstallDates = [];
    this.cdr.markForCheck();
  }

  onPanelGoBackToJob(): void {
    const opportunityId = this.opportunityId;
    this.router.navigate(['/viewer'], opportunityId ? { queryParams: { opportunityId } } : undefined);
  }

  onPanelDateRemoved(event: { date: string; foremanUserId: string }): void {
    this.panelInstallDates = this.panelInstallDates.filter(
      d => !(d.date === event.date && d.foremanUserId === event.foremanUserId),
    );
    this.cdr.markForCheck();
  }

  onPanelProjectChanged(projectId: string): void {
    this.projectId = projectId;
  }

  onSelectionChanged(dates: { date: Date }[]): void {
    if (dates.length === 0) return;
    this.startDate = dates[0].date;
    this.endDate = dates.at(-1)!.date;
    this.calendarService.setDateRange(this.startDate, this.endDate);
    this.loadMonthlyCapacity();
  }

  onSettings(): void {
    this.sideModal.openSettings();
  }

  onDayClicked(event: CapacityDayClickEvent): void {
    const queryParams: Record<string, string> = {
      ...this.buildPassthroughParams(),
    };
    if (this.selectedBranch?.id) {
      queryParams['branchId'] = this.selectedBranch.id;
    }
    queryParams['startDate'] = this.formatDateParam(event.day.date);
    queryParams['foremanId'] = event.group.id;
    this.navigateToWeekly(queryParams);
  }

  onDateHeaderClicked(date: Date): void {
    const queryParams: Record<string, string> = {
      ...this.buildPassthroughParams(),
    };
    if (this.selectedBranch?.id) {
      queryParams['branchId'] = this.selectedBranch.id;
    }
    queryParams['startDate'] = this.formatDateParam(date);
    this.navigateToWeekly(queryParams);
  }

  private loadMonthlyCapacity(): void {
    if (!this.selectedBranch?.id || !this.startDate || !this.endDate) return;

    this.isLoading = true;
    this.isError = false;
    this.cdr.markForCheck();

    this.calendarService
      .getMonthlyCapacity(this.selectedBranch.id, this.startDate, this.endDate)
      .subscribe({
        next: (result) => {
          this.allCrewGroups = this.mapToCapacityCrewGroups(result.crews ?? []);
          this.applyForemanFilter();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.isError = true;
          this.cdr.markForCheck();
        },
      });
  }

  private mapToCapacityCrewGroups(crewInfos: MonthlyCapacityCrewInfo[]): CapacityCrewGroup[] {
    this.crewRoles.clear();
    this.crewAbilities.clear();
    this.crewHasData.clear();

    const result = crewInfos.map((crew) => {
      const canInstall = crew.projectCapability?.canInstallProjectTypes ?? [];
      const cannotInstall = crew.projectCapability?.cannotInstallProjectTypes ?? [];
      const capabilityScope = resolveCapabilityScope(canInstall, cannotInstall);
      const foremanUserId = crew.foremanUserId ?? '';
      const isVisiting = (crew.type as unknown as string) === API_STATUS_VISITING
        || crew.type === CrewScheduleType.Visiting;
      const capacity = crew.capacity ?? [];
      const department = crew.department;
      // Section header rule mirrors CrewScheduleStore.mapToCrewGroups: visiting
      // crews never get a section; home crews are sectioned only by these two
      // department strings. Anything else (including null/undefined) renders
      // ungrouped above sectioned groups.
      const section =
        !isVisiting && (department === 'Sub-Contractor' || department === 'Co-Foreman' || department === 'Field Superintendent')
          ? department
          : undefined;

      // Visiting crews are NOT classified — they bypass the role gate.
      // Home crews fall back to 'Foreman' when department is null/unknown.
      if (!isVisiting) {
        this.crewRoles.set(foremanUserId, MonthlyCapacityComponent.classifyRole(crew.department));
      }
      // Always populate abilities so the gate's set-intersection is the single
      // source of truth. Empty canInstall naturally fails any non-empty selection.
      this.crewAbilities.set(foremanUserId, [...canInstall]);
      // Pre-compute the data short-circuit so the filter pass is O(crews) not O(crews*days).
      this.crewHasData.set(
        foremanUserId,
        capacity.some((d) => (d.isAvailable ?? false) || (d.hasInstall ?? false)),
      );

      return {
        id: foremanUserId,
        name: crew.foremanName ?? '',
        section,
        minIvpd: (crew.ivpd ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }),
        canInstall,
        items: canInstall,
        capabilityScope,
        schedule: this.mapToCapacityDays(capacity),
      };
    });

    this.calendarService.setAvailableAbilities(this.distinctSortedAbilities());

    return result;
  }

  private distinctSortedAbilities(): string[] {
    const distinct = new Set<string>();
    for (const abilities of this.crewAbilities.values()) {
      for (const ability of abilities) {
        if (ability) distinct.add(ability);
      }
    }
    return [...distinct].sort((a, b) => a.localeCompare(b));
  }

  /** Filter composition (mirrors CrewScheduleStore.applyForemanFilter):
   *   ability gate (hard) AND (data short-circuit OR (role gate AND id gate))
   * For monthly-capacity, hasDataInVisibleRange is `day.isAvailable || day.hasInstall`
   * across the schedule. Branch is the outer ring (applied upstream by the load). */
  private applyForemanFilter(): void {
    if (!this.allCrewGroups) {
      this.crewGroups = null;
      this.cdr.markForCheck();
      return;
    }

    this.crewGroups = this.allCrewGroups.filter((group) =>
      this.passesAbilityGate(group)
      && (
        this.hasDataInVisibleRange(group)
        || (this.passesRoleGate(group) && this.passesIdGate(group))
      ),
    );
    this.cdr.markForCheck();
  }

  private hasDataInVisibleRange(group: CapacityCrewGroup): boolean {
    return this.crewHasData.get(group.id) ?? false;
  }

  private passesRoleGate(group: CapacityCrewGroup): boolean {
    const role = this.crewRoles.get(group.id) ?? 'Foreman';
    return this.selectedRoles.includes(role);
  }

  private passesIdGate(group: CapacityCrewGroup): boolean {
    if (this.selectedForemenIds.length === 0) return true;
    return this.selectedForemenIds.includes(group.id);
  }

  private passesAbilityGate(group: CapacityCrewGroup): boolean {
    if (this.selectedAbilities.length === 0) return true;
    const can = this.crewAbilities.get(group.id) ?? [];
    return can.some((ability) => this.selectedAbilities.includes(ability));
  }

  /** Classify a crew's role from the API `department` free-text field.
   * Mirrors `CrewScheduleStore.classifyRole`. Default is 'Foreman'. */
  private static classifyRole(department: string | undefined | null): CalendarRole {
    if (department === 'Sub-Contractor') return 'Sub-Contractor';
    if (department === 'Co-Foreman') return 'Co-Foreman';
    if (department === 'Field Superintendent') return 'Field Superintendent';
    return 'Foreman';
  }

  private mapToCapacityDays(capacity: DayCapacityInfo[]): CapacityDay[] {
    return capacity.map((day) => ({
      date: this.toLocalDate(day.date),
      percentage: day.percentage != null ? Math.round(day.percentage * 100) : null,
      isUnavailable: day.isUnavailable ?? false,
      punchwork: day.hasCustomerSatisfactionVisit ?? false,
    }));
  }

  private toLocalDate(date: Date | string): Date {
    return parseLocalIsoDate(formatUtcIsoDate(date))!;
  }

  private buildPassthroughParams(): Record<string, string> {
    const params: Record<string, string> = {};
    if (this.opportunityId) params['opportunityId'] = this.opportunityId;
    if (this.projectId) params['projectId'] = this.projectId;
    if (this.customerSatisfactionVisit) params['customerSatisfactionVisit'] = 'true';
    return params;
  }

  private navigateToWeekly(queryParams: Record<string, string>): void {
    this.router.navigate(['/calendar'], { queryParams });
  }

  private formatDateParam(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onCalendarViewChanged(calendarView: CalendarView) {
    if (calendarView === 'weekly') {
      this.navigateToWeekly(this.buildPassthroughParams());
    }
  }
}
